import React, { useEffect, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import dayjs from 'dayjs';

import { AppButton } from '@vyce/core/src/components/AppButton';
import { AppKeyboardDatePicker } from '@vyce/core/src/components/inputs';
import { DRCPeriodPayload, DRCProfile } from '@vyce/core/src/types';
import { DRC_EFFECTIVE_FROM_HINT } from '@vyce/core/src/constants';
import { formatDate } from '@vyce/core/src/utils/dates';

import { useStyles } from '../styles';

interface Props {
  profile: DRCProfile | null;
  saving: boolean;
  onSave: (payload: DRCPeriodPayload) => Promise<boolean>;
}

const inputProps = { readOnly: true };

/** Past days are off the calendar, except the one already stored — it stays as it was saved. */
const isBlockedDay = (day: Date | null, earliest: string, stored?: string | null) => {
  if (!day) return false;
  const date = dayjs(day);
  return date.isBefore(earliest, 'day') && !(stored && date.isSame(stored, 'day'));
};

/**
 * The window the reverse charge applies in. Both ends are inclusive.
 *
 * The start date means "applies to pay runs on or after", so a backdated one could only reach a
 * run that has already been calculated — the server answers 422. The calendar greys out past
 * days so the Legend never gets that far.
 *
 * `shouldDisableDate` rather than `disablePast`/`minDate`: those also validate the current value,
 * so a stored date that has since slipped into the past would show as an error it is not.
 */
export const DRCPeriodSection: React.FC<Props> = ({ profile, saving, onSave }) => {
  const classes = useStyles();
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null);
  const [endedOn, setEndedOn] = useState<string | null>(null);

  useEffect(() => {
    setEffectiveFrom(profile?.drc_effective_from || null);
    setEndedOn(profile?.drc_ended_on || null);
  }, [profile]);

  const changed =
    effectiveFrom !== (profile?.drc_effective_from || null) || endedOn !== (profile?.drc_ended_on || null);

  const today = dayjs().format('YYYY-MM-DD');
  const earliestEnd = effectiveFrom && effectiveFrom > today ? effectiveFrom : today;

  const handleSave = () => {
    const payload: DRCPeriodPayload = {};
    if (effectiveFrom !== (profile?.drc_effective_from || null)) payload.effective_from = effectiveFrom;
    if (endedOn !== (profile?.drc_ended_on || null)) payload.ended_on = endedOn;
    onSave(payload);
  };

  return (
    <Box>
      <Typography className={classes.sectionLabel}>Effective period</Typography>

      <Box marginTop={1} display="flex" gridGap={8} flexWrap="wrap">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Box width={200}>
            <AppKeyboardDatePicker
              label="DRC effective from"
              value={effectiveFrom || null}
              onChange={date => setEffectiveFrom(date ? formatDate(date) : null)}
              format="dd/MM/yyyy"
              margin="none"
              fullWidth
              autoOk
              shouldDisableDate={day => isBlockedDay(day, today, profile?.drc_effective_from)}
              InputProps={inputProps}
            />
          </Box>
        </MuiPickersUtilsProvider>

        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Box width={200}>
            {/* ended_on before effective_from is a 409, so the calendar will not offer it. */}
            <AppKeyboardDatePicker
              label="Ends on (optional)"
              value={endedOn || null}
              onChange={date => setEndedOn(date ? formatDate(date) : null)}
              format="dd/MM/yyyy"
              margin="none"
              fullWidth
              autoOk
              shouldDisableDate={day => isBlockedDay(day, earliestEnd, profile?.drc_ended_on)}
              InputProps={inputProps}
            />
          </Box>
        </MuiPickersUtilsProvider>
      </Box>

      <Typography className={classes.hint} style={{ marginTop: 6 }}>
        {DRC_EFFECTIVE_FROM_HINT}
      </Typography>

      <Box marginTop={2}>
        <AppButton
          variant="contained"
          color="primary"
          size="small"
          loading={saving}
          disabled={!changed}
          onClick={handleSave}>
          Save period
        </AppButton>
      </Box>
    </Box>
  );
};
